import { useCallback, useMemo, useState } from 'react'
import { useUserInitializationContext } from '@/features/user'
import { useCrypto } from '@/infra/crypto'
import type { StepConfig } from '../types'

let dismissedSession = false

export interface OnboardingFlags {
  readonly keyRingConfigured: boolean
  readonly apiKeyGenerated: boolean
  readonly hostRegistered: boolean
}

interface ComputedOnboardingState {
  readonly shouldShowOnboarding: boolean
  readonly wizardSteps: readonly StepConfig[]
  readonly isReturningUser: boolean
  readonly isInitializing: boolean
  readonly isOnboardingComplete: boolean
  readonly onboardingFlags: OnboardingFlags | null
}

export interface UseOnboardingStateReturn extends ComputedOnboardingState {
  readonly dismissOnboarding: () => void
}

export function useOnboardingState(): UseOnboardingStateReturn {
  const { userInitialized, onboardingState } = useUserInitializationContext()
  const { keyRingStatus, cryptoInitialized } = useCrypto()
  const [dismissed, setDismissed] = useState(dismissedSession)

  const dismissOnboarding = useCallback(() => {
    dismissedSession = true
    setDismissed(true)
  }, [])

  const computed = useMemo<ComputedOnboardingState>(() => {
    const isInitializing = !userInitialized || !cryptoInitialized

    if (isInitializing) {
      return {
        shouldShowOnboarding: false,
        wizardSteps: [],
        isReturningUser: false,
        isInitializing: true,
        isOnboardingComplete: false,
        onboardingFlags: null,
      }
    }

    if (!onboardingState) {
      return {
        shouldShowOnboarding: false,
        wizardSteps: [],
        isReturningUser: false,
        isInitializing: false,
        isOnboardingComplete: false,
        onboardingFlags: null,
      }
    }

    const { keyRingConfigured, apiKeyGenerated, hostRegistered } = onboardingState

    const allComplete = keyRingConfigured && apiKeyGenerated && hostRegistered
    if (allComplete && keyRingStatus === 'loaded') {
      return {
        shouldShowOnboarding: false,
        wizardSteps: [],
        isReturningUser: false,
        isInitializing: false,
        isOnboardingComplete: true,
        onboardingFlags: { keyRingConfigured, apiKeyGenerated, hostRegistered },
      }
    }

    const isReturningUser = keyRingConfigured && keyRingStatus === 'missing'

    const allSteps: readonly StepConfig[] = [
      {
        id: 'keyRing',
        label: 'Encryption',
        isRequired: true,
        isComplete: keyRingConfigured && keyRingStatus === 'loaded',
      },
      {
        id: 'apiKey',
        label: 'API Key',
        isRequired: false,
        isComplete: apiKeyGenerated,
      },
      {
        id: 'host',
        label: 'Host',
        isRequired: false,
        isComplete: hostRegistered,
      },
    ]

    const wizardSteps = allSteps.filter((step) => !step.isComplete)

    return {
      shouldShowOnboarding: wizardSteps.length > 0,
      wizardSteps,
      isReturningUser,
      isInitializing: false,
      isOnboardingComplete: false,
      onboardingFlags: { keyRingConfigured, apiKeyGenerated, hostRegistered },
    }
  }, [userInitialized, cryptoInitialized, onboardingState, keyRingStatus])

  return {
    ...computed,
    shouldShowOnboarding: computed.shouldShowOnboarding && !dismissed,
    dismissOnboarding,
  }
}
